'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { ContentPage } from '@/lib/supabase';

interface BlogIndexProps {
  initialPosts: ContentPage[];
  categoryLabel?: string;
}

const BlogIndex: React.FC<BlogIndexProps> = ({ initialPosts, categoryLabel }) => {
  const posts = initialPosts;
  const [activeCategory, setActiveCategory] = useState('All');

  // Derive categories from posts' tags (and fall back to category field)
  const categories = ['All', ...Array.from(new Set(
    posts.flatMap((p) => {
      const tags = Array.isArray(p.tags) && p.tags.length > 0 ? p.tags : (p.category ? [p.category] : []);
      return tags;
    })
  )).sort()];

  const filtered = activeCategory === 'All'
    ? posts
    : posts.filter((p) => {
        const tags = Array.isArray(p.tags) && p.tags.length > 0 ? p.tags : (p.category ? [p.category] : []);
        return tags.includes(activeCategory);
      });

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
      <header className="mb-12">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-[var(--color-text)] tracking-tight mb-4">
          {categoryLabel ? `${categoryLabel} Articles` : 'Blog'}
        </h1>
        {categoryLabel && (
          <Link href="/blog" className="text-sm text-[var(--color-primary)] hover:underline mb-2 inline-block">
            &larr; All articles
          </Link>
        )}
        <p className="text-lg text-[var(--color-muted)] max-w-2xl">
          Expert insights on AI grading, tutoring software, and the future of education technology.
        </p>
      </header>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-10">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeCategory === cat
                ? 'bg-[var(--color-primary)] text-white'
                : 'bg-[var(--color-surface-alt)] text-[var(--color-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[var(--color-muted)]">
          No posts yet. Check back soon!
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((post) => (
            <Link
              key={post.slug}
              href={`/${post.slug}`}
              className="group rounded-xl border border-[var(--color-border)] hover:border-[var(--color-border-hover)] transition-all bg-[var(--color-surface)] overflow-hidden"
            >
              {post.image_url && (
                <Image
                  src={post.image_url}
                  alt={post.image_alt || post.title}
                  className="w-full h-44 object-cover"
                  width={400}
                  height={176}
                />
              )}
              <div className="p-5">
                {(post.tags?.[0] || post.category) && (
                  <span className="text-xs font-semibold text-[var(--color-primary)] uppercase tracking-wider">
                    {post.tags?.[0] || post.category}
                  </span>
                )}
                <h2 className="font-heading font-semibold text-[var(--color-text)] group-hover:text-[var(--color-primary)] transition-colors mt-1 mb-2 text-base leading-snug">
                  {post.title}
                </h2>
                <p className="text-sm text-[var(--color-muted)] line-clamp-2">
                  {post.description}
                </p>
                <div className="flex items-center gap-3 mt-4 text-xs text-[var(--color-text-subtle)]">
                  {post.published_at && (
                    <time dateTime={post.published_at}>
                      {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </time>
                  )}
                  {post.reading_time && <span>{post.reading_time} min</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogIndex;
